import React, { useEffect, useRef, useState } from 'react';
import { useLanguageStore } from '../../stores/languageStore';
import LanguageHelper from '../../utils/languageHelper';
import './LanguageDropdown.css';

interface LanguageDropdownProps {
  availableLanguages: string[];
}

export const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ availableLanguages }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const languageStore = useLanguageStore();

  const FlagFor = ({ code, size = 18 }: { code: string; size?: number }) => {
    const flagUrl = LanguageHelper.getFlagUrl(code, 40);
    const languageInfo = LanguageHelper.getLanguage(code);

    if (flagUrl && languageInfo) {
      return (
        <img
          src={flagUrl}
          alt={languageInfo.name}
          width={size}
          height={size * 0.67}
          style={{ objectFit: 'cover', borderRadius: '2px' }}
        />
      );
    }

    // Fallback globe icon
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#e5e7eb" stroke="#94a3b8" />
        <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" stroke="#94a3b8" fill="none" />
      </svg>
    );
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleLanguageChange = (langCode: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    languageStore.setLanguage(langCode);
    setOpen(false);
  };

  return (
    <div className="rc-lang-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`rc-lang-toggle ${open ? 'open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="rc-lang-flag" aria-hidden="true">
          <FlagFor code={languageStore.code} />
        </span>
        <span className="rc-lang-code">{languageStore.code.toUpperCase()}</span>
        <span className="rc-caret" aria-hidden="true">
          <svg width="10" height="6" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 1l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open && (
        <div className="rc-lang-menu" role="listbox">
          {availableLanguages.map((lang) => {
            const languageInfo = LanguageHelper.getLanguage(lang);
            return (
              <button
                key={lang}
                role="option"
                aria-selected={languageStore.code === lang}
                className={`rc-lang-item ${languageStore.code === lang ? 'active' : ''}`}
                onClick={(e) => handleLanguageChange(lang, e)}
              >
                <span className="rc-lang-flag" aria-hidden="true">
                  <FlagFor code={lang} />
                </span>
                <span className="rc-lang-label">
                  {languageInfo?.nativeName || lang.toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageDropdown;
